<template>
  <div class="manage-analytics my-3 mx-0 mx-sm-3">

    <b-nav pills class="mb-5">
      <b-nav-item active>Stats</b-nav-item>
      <b-nav-item :to="{ name: 'ManageAnalyticsReports' }">Reports</b-nav-item>
      <template class="d-flex flex-row" v-if="stats">
        <no-ssr>
          <div class="flex-fill"/>
          <b-nav-form class="mt-3 mt-sm-0">
            <b-checkbox class="mr-3" v-if="isRecruiterAdmin" v-model="viewDetails">See all</b-checkbox>
            <date-picker v-model="dateRange" :shortcuts="false" range type="date" lang="en" format="YYYY-MM-DD"
              class="mr-1 my-auto" :style="{ 'max-width': '210px' }"/>
          </b-nav-form>
        </no-ssr>
      </template>
    </b-nav>

    <div class="d-flex flex-row justify-content-center align-items-center my-5" v-if="$apollo.loading">
      <b-spinner variant="primary"/>
    </div>
    <template v-else-if="stats">
      <b-card-group v-for="(cards, i) in topCards" :key="i">
        <b-card v-for="({ title, label, icon, color, total, target }, j) in cards" :key="`${i}-${j}`"
          :class="{ 'border-top-0': i > 0 }" class="m-0" body-class="d-flex flex-row flex-sm-column flex-md-row">
            <div class="d-flex flex-column flex-fill" v-if="typeof title === 'string' || typeof label === 'string'">
              <div v-if="Array.isArray(label) && label.length === 2"><strong>{{ label[0] }}</strong> {{ label[1] }}</div>
              <div v-else-if="Array.isArray(label)">{{ label.join(' ') }}</div>
              <div class="text-uppercase" v-else-if="[ 'string', 'number' ].includes(typeof label)">{{ label }}</div>

              <div v-else-if="Array.isArray(title) && title.length === 2"><strong>{{ title[0] }}</strong> {{ title[1] }}</div>
              <div v-else-if="Array.isArray(title)">{{ title.join(' ') }}</div>
              <div class="text-uppercase" v-else-if="[ 'string', 'number' ].includes(typeof title)">{{ title }}</div>

              <div v-else>——</div>
              <template v-if="typeof total === 'number' && total > 0">
                <div class="number">
                  {{ total }}
                  <small v-if="isSuperAdmin && typeof target === 'number'" class="target-box"> / {{ target }}</small>
                </div>
                <b-progress v-if="isSuperAdmin && typeof target === 'number'" :max="target" height="6px" striped>
                  <b-progress-bar :value="total" :style="`background-color: ${color || '#eee'} !important`"/>
                </b-progress>
              </template>
              <div v-else class="number">—</div>
            </div>
            <div class="p-2 text-white rounded-circle d-flex flex-row justify-content-center align-items-center" v-if="icon"
              :style="{ 'background-color': color || '#eee', height: '35px', width: '35px' }">
                <font-awesome-icon prefix="fa" :icon="icon"/>
            </div>
        </b-card>
      </b-card-group>

      <div class="chart-container my-5 d-relative h-auto">
        <canvas ref="activityChart"/>
      </div>

      <b-card class="my-5" v-if="viewDetails">
        <b-card-title class="d-flex flex-row justify-content-between align-items-center">
          Distribution by Time
          <div class="d-flex flex-row">
            <b-form-select v-if="distributionTypes" v-model="distributionType" :options="distributionTypes"/>
            <b-dropdown class="ml-1" no-caret right variant="outline-success">
              <template v-slot:button-content>
                <font-awesome-icon icon="download"/>
                <span class="sr-only">Download</span>
              </template>
              <b-dropdown-item @click="() => exportTableAs('csv')">CSV (.csv)</b-dropdown-item>
              <b-dropdown-item @click="() => exportTableAs('xlsx')">Excel (.xlsx)</b-dropdown-item>
            </b-dropdown>
          </div>
        </b-card-title>
        <b-table v-if="Array.isArray(distributionTableFields) && Array.isArray(distributionTableItems)"
          :fields="distributionTableFields" :items="distributionTableItems" primary-key="key"
          responsive striped hover bordered/>
        <b-card-text v-else>
          — —
        </b-card-text>
      </b-card>
    </template>

  </div>
</template>

<script>
import _uniq from 'lodash/uniq';
import gql from 'graphql-tag';
import formatDate from 'date-fns/format';
import DatePicker from 'vue2-datepicker';
import Chart from 'chart.js';
import ExcelJS from 'exceljs';
import startOfMonth from 'date-fns/start_of_month';
import startOfWeek from 'date-fns/start_of_week';
import { mapRoot, mapPreferences } from '@src/mixins/root';

export default {
  props: {
    companyID: String,
  },
  components: {
    DatePicker,
  },
  metaInfo: {
    title: 'Stats',
  },
  data: () => ({
    dateRange: null,
    distributionType: 'WEEKLY',
    distributionTypes: [
      { text: 'Daily', value: 'DAILY' },
      { text: 'Weekly', value: 'WEEKLY' },
      { text: 'Monthly', value: 'MONTHLY' },
    ],
    activityChart: null,
    viewDetails: false,
  }),
  beforeDestroy() {
    if (this.activityChart) {
      this.activityChart = null;
    }
  },
  apollo: {
    stats: {
      query: gql`
        query CompanyStats($isSuperAdmin: Boolean!, $where: StatWhereManyInput!) {
          company {
            id
            stats(where: $where) {
              key
              breakdown
              total
              target @include(if: $isSuperAdmin)
            }
          }
        }
      `,
      variables() {
        const { companyID, isSuperAdmin, topCards, distributionTableKeys } = this;
        const [ startDate, endDate ] = this.dateRange || [];
        const validEndDate = endDate ? new Date(endDate) : undefined;
        if (validEndDate) {
          validEndDate.setDate(validEndDate.getDate() + 1);
        }
        return {
          companyID,
          isSuperAdmin,
          where: {
            key_in: _uniq([
              'usersDailyActive', 'sharesJobTotal', 'applicationsUploadedTotal',
              ...topCards.reduce((ks, cards) => ks.concat(cards.map(({ key }) => key)), []),
              ...distributionTableKeys,
            ]),
            startDate,
            endDate: validEndDate,
          },
        };
      },
      update({ company }) {
        const { stats } = company || {};
        return Array.isArray(stats) ? stats : [];
      },
      error(err) {
        this.$notifyErr(err);
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isLoggedIn', 'isSuperAdmin', 'isRecruiterAdmin' ]),
    ...mapPreferences([
      'isReferralsFeatureEnabled', 'canSyncContacts',
      'isInternalMobilityEnabled',
      'isContentShareToolEnabled',
    ]),
    earliestDay() {
      const collectDates = (list, { breakdown }) => {
        (Array.isArray(breakdown) ? breakdown : []).filter(r => r && r.day).forEach(({ day }) => list.push(day));
        return list;
      };

      const stats = Array.isArray(this.stats) ? this.stats : [];
      const allDates = _uniq(stats.reduce(collectDates, [])).sort().reverse();
      return allDates.slice(allDates.length - 1).shift();
    },
    everyDayList() {
      const { earliestDay } = this;
      const everyDay = [];

      for (let day = new Date(earliestDay); day <= new Date(); day.setDate(day.getDate() + 1)) {
        everyDay.push(formatDate(day, 'YYYY-MM-DD'));
      }

      return everyDay;
    },
    topCards() {
      const {
        isReferralsFeatureEnabled, canSyncContacts,
        isInternalMobilityEnabled,
        isContentShareToolEnabled,
        viewDetails,
      } = this;

      const rows = [];

      const getValue = key => {
        const { total } = (Array.isArray(this.stats) ? this.stats : []).find(stat => stat && stat.key === key) || {};
        return total || null;
      };
      const getTimeVarianceValue = key => {
        const { breakdown } = (Array.isArray(this.stats) ? this.stats : []).find(stat => stat && stat.key === key) || {};
        const [ { count: previous } = {}, { count: current } = {} ] = (Array.isArray(breakdown) && breakdown.length ? breakdown.slice(breakdown.length - 2) : []);
        return Math.max(previous || 0, current || 0);
      };

      const totalShares = (getValue('sharesJobTotal') || 0) + (getValue('applicationsUploadedTotal') || 0);

      if (isReferralsFeatureEnabled) {
        if (canSyncContacts) {
          rows.push([
            { key: 'usersEmployees', ...descriptions['usersEmployees'], total: getValue('usersEmployees') },
            { key: 'invitesSent', ...descriptions['invitesSent'], total: getValue('invitesSent') },
            { key: 'contactsTotal', ...descriptions['contactsTotal'], total: getValue('contactsTotal') },
            { key: 'usersMonthlyActive', ...descriptions['usersMonthlyActive'], total: getTimeVarianceValue('usersMonthlyActive') },
          ], [
            { key: 'referralsTotal', ...descriptions['referralsTotal'], total: totalShares },
            { key: 'applicationsTotal', ...descriptions['applicationsTotal'], total: getValue('applicationsTotal') },
            { key: 'applicationsInterviewTotal', ...descriptions['applicationsInterviewTotal'], total: getValue('applicationsInterviewTotal') },
            { key: 'applicationsHiredTotal', ...descriptions['applicationsHiredTotal'], total: getValue('applicationsHiredTotal') },
          ]);

          if (viewDetails) {
            rows.push([
              { key: 'referralsRecruiterAccepted', ...descriptions['referralsRecruiterAccepted'], total: getValue('referralsRecruiterAccepted') },
              { key: 'applicationsRecruiterReferralsTotal', ...descriptions['applicationsRecruiterReferralsTotal'], total: getValue('applicationsRecruiterReferralsTotal') },
              { key: 'applicationsInterviewRecruiterReferralsTotal', ...descriptions['applicationsInterviewRecruiterReferralsTotal'], total: getValue('applicationsInterviewRecruiterReferralsTotal') },
              { key: 'applicationsHiredRecruiterReferralsTotal', ...descriptions['applicationsHiredRecruiterReferralsTotal'], total: getValue('applicationsHiredRecruiterReferralsTotal') },
            ], [
              { key: 'referralsEmployeeTotal', ...descriptions['referralsEmployeeTotal'], total: getValue('referralsEmployeeTotal') },
              { key: 'applicationsEmployeeReferralsTotal', ...descriptions['applicationsEmployeeReferralsTotal'], total: getValue('applicationsEmployeeReferralsTotal') },
              { key: 'applicationsInterviewEmployeeReferralsTotal', ...descriptions['applicationsInterviewEmployeeReferralsTotal'], total: getValue('applicationsInterviewEmployeeReferralsTotal') },
              { key: 'applicationsHiredEmployeeReferralsTotal', ...descriptions['applicationsHiredEmployeeReferralsTotal'], total: getValue('applicationsHiredEmployeeReferralsTotal') },
            ], [
              { key: 'sharesJobTotal', ...descriptions['sharesJobTotal'], total: getValue('sharesJobTotal') },
              { key: 'applicationsSharesTotal', ...descriptions['applicationsSharesTotal'], total: getValue('applicationsSharesTotal') },
              { key: 'applicationsInterviewSharesTotal', ...descriptions['applicationsInterviewSharesTotal'], total: getValue('applicationsInterviewSharesTotal') },
              { key: 'applicationsHiredSharesTotal', ...descriptions['applicationsHiredSharesTotal'], total: getValue('applicationsHiredSharesTotal') },
            ], [
              { key: 'mobileAppInstallsTotal', ...descriptions['mobileAppInstallsTotal'], total: getValue('mobileAppInstallsTotal') },
              { key: 'pushNotificationsEnabled', ...descriptions['pushNotificationsEnabled'], total: getValue('pushNotificationsEnabled') },
            ]);
          }
        } else {
          rows.push([
            { key: 'usersEmployees', ...descriptions['usersEmployees'], total: getValue('usersEmployees') },
            { key: 'invitesSent', ...descriptions['invitesSent'], total: getValue('invitesSent') },
            { key: 'usersWeeklyActive', ...descriptions['usersWeeklyActive'], total: getTimeVarianceValue('usersWeeklyActive') },
            { key: 'usersMonthlyActive', ...descriptions['usersMonthlyActive'], total: getTimeVarianceValue('usersMonthlyActive') },
          ], [
            { key: 'sharesJobTotal', ...descriptions['sharesJobTotal'], total: totalShares },
            { key: 'applicationsTotal', ...descriptions['applicationsTotal'], total: getValue('applicationsTotal') },
            { key: 'applicationsInterviewTotal', ...descriptions['applicationsInterviewTotal'], total: getValue('applicationsInterviewTotal') },
            { key: 'applicationsHiredTotal', ...descriptions['applicationsHiredTotal'], total: getValue('applicationsHiredTotal') },
          ], [
            { key: 'mobileAppInstallsTotal', ...descriptions['mobileAppInstallsTotal'], total: getValue('mobileAppInstallsTotal') },
            { key: 'pushNotificationsEnabled', ...descriptions['pushNotificationsEnabled'], total: getValue('pushNotificationsEnabled') },
          ]);

          if (viewDetails) {
            rows.push([
              { key: 'sharesJobTotal', ...descriptions['sharesJobTotal'], total: getValue('sharesJobTotal') },
              { key: 'applicationsSharesTotal', ...descriptions['applicationsSharesTotal'], total: getValue('applicationsSharesTotal') },
              { key: 'applicationsInterviewSharesTotal', ...descriptions['applicationsInterviewSharesTotal'], total: getValue('applicationsInterviewSharesTotal') },
              { key: 'applicationsHiredSharesTotal', ...descriptions['applicationsHiredSharesTotal'], total: getValue('applicationsHiredSharesTotal') },
            ]);
          }
        }
      }

      if (isInternalMobilityEnabled) {
        rows.push([
          { key: 'usersEmployees', ...descriptions['usersEmployees'], total: getValue('usersEmployees') },
          { key: 'usersTalents', ...descriptions['usersTalents'], total: getValue('usersTalents') },
          { key: 'activeMentorsTotal', ...descriptions['activeMentorsTotal'], total: getValue('activeMentorsTotal') }
        ], [
          { key: 'openJobsTotal', ...descriptions['openJobsTotal'], total: getValue('openJobsTotal') },
          { key: 'projectsTotal', ...descriptions['projectsTotal'], total: getValue('projectsTotal') },
          { key: 'projectRolesTotal', ...descriptions['projectRolesTotal'], total: getValue('projectsTotal') }
        ], [
          { key: 'matchedJobsTotal', ...descriptions['matchedJobsTotal'], total: getValue('matchedJobsTotal') },
          { key: 'applicationsJobIMTotal', ...descriptions['applicationsJobIMTotal'], total: getValue('applicationsJobIMTotal') },
          { key: 'applicationsJobInterviewIMTotal', ...descriptions['applicationsJobInterviewIMTotal'], total: getValue('applicationsJobInterviewIMTotal') },
          { key: 'applicationsJobHiredIMTotal', ...descriptions['applicationsJobHiredIMTotal'], total: getValue('applicationsJobHiredIMTotal') },
        ], [
          { key: 'matchedRolesTotal', ...descriptions['matchedRolesTotal'], total: getValue('matchedRolesTotal') },
          { key: 'applicationsRoleIMTotal', ...descriptions['applicationsRoleIMTotal'], total: getValue('applicationsRoleIMTotal') },
          { key: 'applicationsRoleInterviewIMTotal', ...descriptions['applicationsRoleInterviewIMTotal'], total: getValue('applicationsRoleInterviewIMTotal') },
          { key: 'applicationsRoleHiredIMTotal', ...descriptions['applicationsRoleHiredIMTotal'], total: getValue('applicationsRoleHiredIMTotal') },
        ], [
          { key: 'matchedMentorsTotal', ...descriptions['matchedMentorsTotal'], total: getValue('matchedMentorsTotal') },
          { key: 'activeMentorshipsTotal', ...descriptions['activeMentorshipsTotal'], total: getValue('activeMentorshipsTotal') },
        ]);
      }

      if (isContentShareToolEnabled) {
        rows.push([
          { key: 'contentItemsTotal', ...descriptions['contentItemsTotal'], total: getValue('contentItemsTotal') },
          { key: 'contentSharesTotal', ...descriptions['contentSharesTotal'], total: getValue('contentSharesTotal') },
          { key: 'contentViewsTotal', ...descriptions['contentViewsTotal'], total: getValue('contentViewsTotal') },
        ]);
      }

      return rows;
    },
    activityChartData() {
      if (Array.isArray(this.stats)) {
        const timeline = this.everyDayList.slice().reverse().slice(0, 200); // Last 200 days

        const datasets = [];
        if (this.isInternalMobilityEnabled) {
          datasets.push({
            label: 'Active Users',
            borderColor: descriptions.usersDailyActive.color,
            data: this.stats.find(r => r && r.key === 'usersDailyActive'),
          });
          datasets.push({
            label: 'Active Mentors',
            borderColor: descriptions.activeMentorsTotal.color,
            data: this.stats.find(r => r && r.key === 'activeMentorsTotal'),
          });
          datasets.push({
            label: 'Active Mentorships',
            borderColor: descriptions.activeMentorshipsTotal.color,
            data: this.stats.find(r => r && r.key === 'activeMentorshipsTotal'),
          });
          datasets.push({
            label: 'Open Jobs',
            borderColor: descriptions.openJobsTotal.color,
            data: this.stats.find(r => r && r.key === 'openJobsTotal'),
          });
          datasets.push({
            label: 'Project Roles',
            borderColor: descriptions.projectRolesTotal.color,
            data: this.stats.find(r => r && r.key === 'projectRolesTotal'),
          });
          datasets.push({
            label: 'Applications (Job)',
            borderColor: descriptions.applicationsJobIMTotal.color,
            data: this.stats.find(r => r && r.key === 'applicationsJobIMTotal'),
          });
          datasets.push({
            label: 'Applications (Role)',
            borderColor: descriptions.applicationsRoleIMTotal.color,
            data: this.stats.find(r => r && r.key === 'applicationsRoleIMTotal'),
          });
        } else {
          datasets.push({
            label: 'Active Users',
            borderColor: descriptions.usersDailyActive.color,
            data: this.stats.find(r => r && r.key === 'usersDailyActive'),
          });
          datasets.push({
            label: 'Shares',
            borderColor: descriptions.sharesJobTotal.color,
            data: this.stats.find(r => r && r.key === 'sharesJobTotal') || {},
          });
        }

        return {
          labels: timeline.map(day => formatDate(new Date(day), 'MMM Do')),
          datasets: datasets.reduce((list, set) => {
            if (set && set.data && Array.isArray(set.data.breakdown)) {
              set.data = timeline.map(day => {
                const { count } = set.data.breakdown.find(r => r.day === day) || {};
                return count || 0;
              });
            }
            return list.concat([ set ]);
          }, []),
        };
      } else {
        return null;
      }
    },
    distributionTableKeys() {
      const {
        isReferralsFeatureEnabled, canSyncContacts,
        isInternalMobilityEnabled,
        isContentShareToolEnabled,
      } = this;

      const keys = [
        'usersDailyActive',
        'usersWeeklyActive',
        'usersMonthlyActive',
      ];

      if (isReferralsFeatureEnabled) {
        if (canSyncContacts) {
          keys.push(...[
            'usersEmployees',
            'invitesSent',
            'contactsTotal',
            'referralsTotal',
            'applicationsTotal',
            'applicationsInterviewTotal',
            'applicationsHiredTotal',
            'referralsRecruiterAccepted',
            'applicationsRecruiterReferralsTotal',
            'applicationsInterviewRecruiterReferralsTotal',
            'applicationsHiredRecruiterReferralsTotal',
            'referralsEmployeeTotal',
            'applicationsEmployeeReferralsTotal',
            'applicationsInterviewEmployeeReferralsTotal',
            'applicationsHiredEmployeeReferralsTotal',
            'sharesJobTotal',
            'applicationsSharesTotal',
            'applicationsInterviewSharesTotal',
            'applicationsHiredSharesTotal',
          ]);
        } else {
          keys.push(...[
            'usersEmployees',
            'invitesSent',
            'sharesJobTotal',
            'applicationsTotal',
            'applicationsInterviewTotal',
            'applicationsHiredTotal',
            'sharesJobTotal',
            'applicationsSharesTotal',
            'applicationsInterviewSharesTotal',
            'applicationsHiredSharesTotal',
            'mobileAppInstallsTotal',
            'pushNotificationsEnabled'
          ]);
        }
      }

      if (isContentShareToolEnabled) {
        keys.push(...[
          'contentItemsTotal',
          'contentSharesTotal',
          'contentViewsTotal',
        ]);
      }

      if (isInternalMobilityEnabled) {
        keys.push(...[
          'usersEmployees',
          'usersTalents',
          'openJobsTotal',
          'projectsTotal',
          'projectRolesTotal',
          'matchedJobsTotal',
          'applicationsJobIMTotal',
          'applicationsJobInterviewIMTotal',
          'applicationsJobHiredIMTotal',
          'matchedRolesTotal',
          'applicationsRoleIMTotal',
          'applicationsRoleInterviewIMTotal',
          'applicationsRoleHiredIMTotal',
          'activeMentorsTotal',
          'matchedMentorsTotal',
          'activeMentorshipsTotal'
        ]);
      }

      return keys;
    },
    distributionTableFields() {
      const { everyDayList } = this;

      const fields = [
        {
          key: 'key',
          label: 'Name',
          formatter: key => descriptions[key] && descriptions[key].title ? descriptions[key].title : key,
        }
      ];

      const defaultDateCol = {
        // key: null,
        sortable: true,
      };

      everyDayList.slice().reverse().forEach(day => {
        switch (this.distributionType) {
          case 'DAILY':
            const key = formatDate(day, 'YYYY-MM-DD');
            fields.push({ key, ...defaultDateCol });
            break;
          case 'WEEKLY': {
            const key = formatDate(startOfWeek(day), 'YYYY-MM-DD');
            if (!fields.find(f => f && f.key === key)) {
              fields.push({ key, ...defaultDateCol });
            }
            break;
          }
          case 'MONTHLY': {
            const key = formatDate(startOfWeek(day), 'YYYY-MM');
            if (!fields.find(f => f && f.key === key)) {
              fields.push({ key, ...defaultDateCol });
            }
          }
        }
      });

      return fields;
    },
    distributionTableItems() {
      const { everyDayList } = this;
      const distributionKeys = this.distributionTableKeys.slice();

      switch (this.distributionType) {
        case 'DAILY': {
          distributionKeys.splice(distributionKeys.indexOf('usersWeeklyActive'), 1);
          distributionKeys.splice(distributionKeys.indexOf('usersMonthlyActive'), 1);
          break;
        }
        case 'WEEKLY': {
          distributionKeys.splice(distributionKeys.indexOf('usersDailyActive'), 1);
          distributionKeys.splice(distributionKeys.indexOf('usersMonthlyActive'), 1);
          break;
        }
        case 'MONTHLY': {
          distributionKeys.splice(distributionKeys.indexOf('usersDailyActive'), 1);
          distributionKeys.splice(distributionKeys.indexOf('usersWeeklyActive'), 1);
          break;
        }
      }

      return distributionKeys.map(key => {
        const { breakdown } = (Array.isArray(this.stats) ? this.stats : []).find(s => s && s.key === key) || {};
        const cols = {
          key
        };

        if ([ 'usersDailyActive', 'usersWeeklyActive', 'usersMonthlyActive' ].includes(key)) {
          everyDayList.forEach(day => {
            switch (key) {
              case 'usersDailyActive': {
                const date = formatDate(day, 'YYYY-MM-DD');
                const { count } = (Array.isArray(breakdown) ? breakdown : []).find(r => r && r.day === date) || {};
                cols[date] = (count || 0);
                break;
              }
              case 'usersWeeklyActive': {
                const colName = formatDate(day, 'YYYY-MM-DD');
                const weekNo = formatDate(startOfWeek(day), 'YYYY-WW');
                const { count } = (Array.isArray(breakdown) ? breakdown : []).find(r => r && r.week === weekNo) || {};
                cols[colName] = (count || 0);
                break;
              }
              case 'usersMonthlyActive': {
                const date = formatDate(startOfMonth(day), 'YYYY-MM');
                const { count } = (Array.isArray(breakdown) ? breakdown : []).find(r => r && r.month === date) || {};
                cols[date] = (count || 0);
                break;
              }
            }
          });
        } else {
          const availableData = (Array.isArray(breakdown) ? breakdown : []).filter(r => r && r.day && r.count);
          if (availableData.length) {
            console.log({ key, availableData, everyDayList });
            everyDayList.forEach(day => {
              const fday = formatDate(day, 'YYYY-MM-DD');
              const { count } = availableData.find(r => r.day === fday) || {};

              switch (this.distributionType) {
                case 'DAILY': {
                  const date = formatDate(day, 'YYYY-MM-DD');
                  cols[date] = (count || 0);
                  break;
                }
                case 'WEEKLY': {
                  const date = formatDate(startOfWeek(day), 'YYYY-MM-DD');
                  cols[date] = (cols[date] || 0) + (count || 0);
                  break;
                }
                case 'MONTHLY': {
                  const date = formatDate(startOfMonth(day), 'YYYY-MM');
                  cols[date] = (cols[date] || 0) + (count || 0);
                  break;
                }
              }
            });
          }
        }

        return cols;
      });
    },
  },
  methods: {

    async exportTableAs(type) {
      try {
        this.$assert([ 'csv', 'xlsx' ].includes(type), new Error('Invalid file export type chosen'));

        const {
          company, distributionType, distributionTypes,
          distributionTableFields: fields, distributionTableItems: items,
        } = this;
        this.$assert(Array.isArray(fields) && fields.length, 'Expected fields to be an array');
        this.$assert(Array.isArray(items) && items.length, 'Expected items to be an array');

        const workbook = new ExcelJS.Workbook();
        // workbook.creator = `${this.user.fullName} <${this.user.emailAddress}>`;
        const worksheet = workbook.addWorksheet('Table Export');

        worksheet.columns = fields.map(({ key, label, formatter }) => ({
          header: label || (typeof formatter === 'function' ? formatter(key) : null) || key,
          key
        }));
        items.forEach(({ key, ...row }) => {
          const { formatter: formatKey } = fields[0] || {};
          const label = typeof formatKey === 'function' ? formatKey(key) : null;
          worksheet.addRow({ key: label || key, ...row });
        });

        const buffer = await workbook[type].writeBuffer();
        const blob = new Blob([ buffer ], { type: type === 'xlsx' ? 'application/vnd.ms-excel' : 'text/csv' });

        let [ startDate, endDate ] = this.dateRange || [];
        startDate = startDate ? formatDate(startDate, 'DD/MM/YYYY') : '';
        endDate = endDate ? formatDate(endDate, 'DD/MM/YYYY') : '';

        const link = document.createElement('a');
        link.download = (a => `${a.filter(b => b).join('-')}.${type}`)([
          'RL-Export',
          company.name.replace(/ /g, '-'),
          (distributionTypes.find(r => r.value === distributionType) || {}).text,
          startDate,
          endDate
        ]);
        link.target = 'blank';
        link.href = URL.createObjectURL(blob);
        link.click();
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err, { prepend: 'Failed to export table:' });
      }
    },

  },
  watch: {
    activityChartData: {
      deep: true,
      async handler(data) {
        if (data) {
          await this.$nextTick();
          const { activityChart } = this.$refs;
          this.activityChart = new Chart(activityChart.getContext('2d'), {
            type: 'line',
            data,
          });
        } else {
          this.activityChart = null;
        }
      },
    },
  },
};

const descriptions = {
  invitesSent: {
    label: [ 'Total', 'Invites' ],
    title: 'Invites Sent',
    icon: 'envelope',
    color: '#58B19F',
  },
  usersEmployees: {
    label: [ 'Total', 'Users' ],
    title: 'Users',
    icon: 'users',
    color: '#e17055',
  },
  usersSynced: {
    label: [ 'Synced', 'Users' ],
    title: 'Synced Users',
    icon: 'sync',
    color: '#54a0ff',
  },
  contactsTotal: {
    label: [ 'Total', 'Contacts' ],
    title: 'Total Contacts',
    icon: 'user-tie',
    color: '#0984e3',
  },
  usersLoginTotal: {
    label: [ 'Total', 'Logins' ],
    title: 'Logins',
    icon: 'sign-in-alt',
    color: '#ff9f43',
  },

  usersDailyActive: {
    label: [ 'Daily', 'Users' ],
    title: 'Active Users',
    icon: 'users',
    color: '#e17055',
  },
  usersWeeklyActive: {
    label: [ 'Weekly', 'Users' ],
    title: 'Active Users',
    icon: 'users',
    color: '#e17055',
  },
  usersMonthlyActive: {
    label: [ 'Monthly', 'Users' ],
    title: 'Active Users',
    icon: 'users',
    color: '#e17055',
  },

  applicationsTotal: {
    label: [ 'Total', 'Applications' ],
    title: 'Total Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsInterviewTotal: {
    label: [ 'Total', 'Interviews' ],
    title: 'Total Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsHiredTotal: {
    label: [ 'Total', 'Hires' ],
    title: 'Total Hires',
    icon: 'handshake',
    color: '#34ace0',
  },
  applicationsUploadedTotal: {
    label: [ 'Uploaded', 'Applications' ],
    title: 'Total Uploaded Applications',
    icon: 'handshake',
    color: '#34ace0',
  },

  referralsTotal: {
    label: [ 'Total', 'Referrals' ],
    title: 'Referrals',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },
  sharesJobTotal: {
    label: [ 'Total', 'Shares' ],
    title: 'Shares',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },

  projectsTotal: {
    label: [ 'Total', 'Projects' ],
    title: 'Shares',
    icon: 'building',
    color: '#0984e3',
  },
  openJobsTotal: {
    label: [ 'Total', 'Open Jobs' ],
    title: 'Shares',
    icon: 'user-tie',
    color: '#ef0505',
  },
  activeMentorsTotal: {
    label: [ 'Active', 'Mentors' ],
    title: 'Active Mentors',
    icon: 'users',
    color: '#41c5cc',
  },
  activeMentorshipsTotal: {
    label: [ 'Active', 'Mentorships' ],
    title: 'Active Mentorships',
    icon: 'handshake',
    color: '#10a05a',
  },

  referralsRecruiterAccepted: {
    label: [ 'Accepted Recruiter', 'Referrals' ],
    title: 'Recruiter Referrals → Total Accepted',
    icon: 'hand-lizard',
    color: '#ff4757',
  },
  applicationsRecruiterReferralsTotal: {
    label: [ 'Total Recruiter Referrals', 'Applications' ],
    title: 'Recruiter Referrals → Total Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsInterviewRecruiterReferralsTotal: {
    label: [ 'Total Recruiter Referrals', 'Interviews' ],
    title: 'Recruiter Referrals → Total Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsHiredRecruiterReferralsTotal: {
    label: [ 'Total Recruiter Referrals', 'Hires' ],
    title: 'Recruiter Referrals → Total Hires',
    icon: 'handshake',
    color: '#34ace0',
  },

  referralsEmployeeTotal: {
    label: [ 'Total', 'Referrals' ],
    title: 'Referrals',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },
  applicationsEmployeeReferralsTotal: {
    label: [ 'Total Referral', 'Applications' ],
    title: 'Referrals → Total Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsInterviewEmployeeReferralsTotal: {
    label: [ 'Total Referral', 'Interviews' ],
    title: 'Referrals → Total Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsHiredEmployeeReferralsTotal: {
    label: [ 'Total Referral', 'Hires' ],
    title: 'Referrals → Total Hires',
    icon: 'handshake',
    color: '#34ace0',
  },

  applicationsSharesTotal: {
    label: [ 'Total Applications', 'from Shares' ],
    title: 'Shares → Total Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsInterviewSharesTotal: {
    label: [ 'Total Interviews', 'from Shares' ],
    title: 'Shares → Total Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsHiredSharesTotal: {
    label: [ 'Total Hires', 'from Shares' ],
    title: 'Shares → Total Hires',
    icon: 'handshake',
    color: '#34ace0',
  },

  contentItemsTotal: {
    label: [ 'Total', 'Content Added' ],
    title: 'Total Content Added',
    icon: 'file-alt',
    color: '#fa8231',
  },
  contentSharesTotal: {
    label: [ 'Total', 'Content Shares' ],
    title: 'Total Content Shares',
    icon: 'share',
    color: '#575fcf',
  },
  contentViewsTotal: {
    label: [ 'Total', 'Content Views' ],
    title: 'Total Content Views',
    icon: 'eye',
    color: '#34ace0',
  },
  usersTalents: {
    label: [ 'Total', 'Talents' ],
    title: 'Talents',
    icon: 'users',
    color: '#facc15',
  },
  projectRolesTotal: {
    label: [ 'Total', 'Project Roles' ],
    title: 'Project Roles',
    icon: 'user-tie',
    color: '#2dd4bf',
  },
  matchedJobsTotal: {
    label: [ 'Total', 'Job Matches' ],
    title: 'Job Matches',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },
  applicationsJobIMTotal: {
    label: [ 'Total Job', 'Applications' ],
    title: 'Job Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsJobInterviewIMTotal: {
    label: [ 'Total Job', 'Interviews' ],
    title: 'Job Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsJobHiredIMTotal: {
    label: [ 'Total Job', 'Hires' ],
    title: 'Job Hires',
    icon: 'handshake',
    color: '#34ace0',
  },
  matchedRolesTotal: {
    label: [ 'Total', 'Role Matches' ],
    title: 'Role Matches',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },
  applicationsRoleIMTotal: {
    label: [ 'Total Role', 'Applications' ],
    title: 'Role Applications',
    icon: 'address-card',
    color: '#fa8231',
  },
  applicationsRoleInterviewIMTotal: {
    label: [ 'Total Role', 'Interviews' ],
    title: 'Role Interviews',
    icon: 'comments',
    color: '#575fcf',
  },
  applicationsRoleHiredIMTotal: {
    label: [ 'Total Role', 'Hires' ],
    title: 'Role Hires',
    icon: 'handshake',
    color: '#34ace0',
  },
  matchedMentorsTotal: {
    label: [ 'Total', 'Mentor Matches' ],
    title: 'Mentor Matches',
    icon: 'hand-holding-heart',
    color: '#10a05a',
  },

  mobileAppInstallsTotal: {
    label: [ 'Total', 'Mobile Installs' ],
    title: 'Mobile Installs',
    icon: 'mobile',
    color: '#000000'
  },

  pushNotificationsEnabled: {
    label: [ 'Push Notifications', 'Enabled' ],
    title: 'Notifications Enabled',
    icon: 'mobile',
    color: '#000000'
  },

  allEmailsSent: {
    title: 'Emails Sent',
  },
  allEmailsOpened: {
    title: 'Emails Opened',
  },
  allEmailsClicked: {
    title: 'Emails Clicked',
  },
  weeklyEmailsSent: {
    title: 'Weekly Emails Sent',
  },
  weeklyEmailsOpened: {
    title: 'Weekly Emails Opened',
  },
  weeklyEmailsClicked: {
    title: 'Weekly Emails Clicked',
  },
  reengagementEmailsSent: {
    title: 'Re-engagement Emails Sent',
  },
  reengagementEmailsOpened: {
    title: 'Re-engagement Emails Opened',
  },
  reengagementEmailsClicked: {
    title: 'Re-engagement Emails Clicked',
  },
  manualEmailsSent: {
    title: 'Manual Emails Sent',
  },
  manualEmailsOpened: {
    title: 'Manual Emails Opened',
  },
  manualEmailsClicked: {
    title: 'Manual Emails Clicked',
  },
};
</script>

<style lang="scss">
.manage-analytics {

  .card-group .card {
    .number {
      text-transform: uppercase;
      font-size: 20px;
      font-weight: bold;
    }

    .target-box {
      font-size: 14px;
      color: #666;
    }
  }

  .mx-datepicker {
    float: right;

    .mx-input {
      height: 37px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      box-shadow: none;
    }
  }

  tr > th:first-child {
    // Used to fix the Name column width
    min-width: 250px !important;
  }

  .d-relative {
    position: relative;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Recruiter/Stats.vue